import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Wand2, Plus, Trash2, Copy, Facebook, Download, Bell, CheckCircle2, Loader2, Video, Image as ImageIcon, Type, Mail } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../../components/ui/switch";
import { api, imgUrl } from "../../../lib/api";
import BestTimeCard from "./BestTimeCard";
import { useFb, inp, card, btn, btnMain, copyText, localToday, shiftDay, toIso, Label, Empty, PageSelect } from "./shared";

const KIND_ICON = { video: Video, picture: ImageIcon, text: Type };

const postToPage = (item, page) => {
  copyText(item.caption, "Caption copied — paste it on Facebook and attach the media");
  window.open(page?.url || "https://www.facebook.com", "_blank", "noopener,noreferrer");
};

const Media = ({ item, posts, pics }) => {
  if (item.kind === "video") {
    const p = posts.find((x) => x.n === item.post_n);
    return p ? <img src={imgUrl(p.thumb_url)} alt="" className="h-28 w-16 shrink-0 rounded-lg object-cover" /> : null;
  }
  if (item.kind === "picture") {
    const p = pics.find((x) => x.id === item.picture_id);
    return p ? <img src={imgUrl(p.url)} alt="" className="h-20 w-20 shrink-0 rounded-lg object-cover" /> : null;
  }
  return null;
};

const mediaUrl = (item, posts, pics) => (item.kind === "video" ? posts.find((x) => x.n === item.post_n)?.video_url
  : item.kind === "picture" ? pics.find((x) => x.id === item.picture_id)?.url : "");

const Item = ({ s, page, posts, pics, update, remove }) => {
  const I = KIND_ICON[s.kind] || Type;
  const url = mediaUrl(s, posts, pics);
  const label = s.kind === "video" ? `Video Post ${s.post_n}` : s.kind === "picture" ? (pics.find((x) => x.id === s.picture_id)?.title || "Picture") : "Text post";
  return (
    <div className={`${card} flex gap-4 ${s.posted ? "opacity-70" : ""}`} data-testid={`fb-sched-item-${s.id}`}>
      <Media item={s} posts={posts} pics={pics} />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          <span className="inline-flex items-center gap-1 rounded-full bg-[#e8f8f4] px-2.5 py-0.5 font-semibold text-[#0b6f65]"><I size={13} />{label}</span>
          <span className="font-semibold text-[#0f2e2a]">{page?.name || "No page"}</span>
          <input type="time" value={s.time} onChange={(e) => update(s.id, { time: e.target.value, at: toIso(s.date, e.target.value) })} className="rounded-md border border-[#dcebe8] px-2 py-0.5 text-[13px]" data-testid={`fb-sched-time-${s.id}`} />
        </div>
        <textarea rows={3} value={s.caption} onChange={(e) => update(s.id, { caption: e.target.value }, false)} onBlur={(e) => update(s.id, { caption: e.target.value })} className={inp} data-testid={`fb-sched-caption-${s.id}`} />
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => postToPage(s, page)} className={btnMain} data-testid={`fb-sched-post-${s.id}`}><Facebook size={14} />Copy & open page</button>
          <button onClick={() => copyText(s.caption, "Caption copied")} className={btn} data-testid={`fb-sched-copy-${s.id}`}><Copy size={13} />Copy</button>
          {url && <a href={imgUrl(url)} download target="_blank" rel="noreferrer" className={btn} data-testid={`fb-sched-download-${s.id}`}><Download size={13} />Media</a>}
          <label className="flex items-center gap-1.5 text-[12.5px] text-[#3d504d]"><Switch checked={!!s.remind} onCheckedChange={(v) => update(s.id, { remind: v })} data-testid={`fb-sched-remind-${s.id}`} /><Bell size={13} />Email me{s.reminder_sent ? " (sent)" : ""}</label>
          <label className="flex items-center gap-1.5 text-[12.5px] font-medium text-[#0b6f65]"><Switch checked={!!s.posted} onCheckedChange={(v) => update(s.id, { posted: v })} data-testid={`fb-sched-posted-${s.id}`} /><CheckCircle2 size={13} />Posted</label>
          <button onClick={() => remove(s.id)} className={`${btn} ml-auto`} data-testid={`fb-sched-delete-${s.id}`}><Trash2 size={13} /></button>
        </div>
      </div>
    </div>
  );
};

const AddForm = ({ day, pages, posts, pics, onAdd }) => {
  const [f, setF] = useState({ page_id: "", kind: "video", post_n: 1, picture_id: "", time: "12:00", caption: "" });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async () => {
    if (!f.page_id) return toast.error("Choose a page");
    const page = pages.find((p) => p.id === f.page_id);
    const base = f.kind === "video" ? posts.find((p) => p.n === Number(f.post_n))?.caption : f.kind === "picture" ? pics.find((p) => p.id === f.picture_id)?.caption : "";
    const caption = (f.caption || base || "").replaceAll("src=facebook", `src=fb_${page?.slug}`);
    await onAdd({ ...f, post_n: Number(f.post_n), date: day, at: toIso(day, f.time), caption });
    setF((x) => ({ ...x, caption: "" }));
    toast.success("Added to schedule");
  };
  return (
    <div className={`${card} grid gap-3 md:grid-cols-6`} data-testid="fb-sched-add-form">
      <Label t="Page" className="md:col-span-2"><PageSelect pages={pages} value={f.page_id} onChange={(v) => set("page_id", v)} testid="fb-sched-add-page" /></Label>
      <Label t="Type"><select value={f.kind} onChange={(e) => set("kind", e.target.value)} className={inp} data-testid="fb-sched-add-kind"><option value="video">Video</option><option value="picture">Picture</option><option value="text">Text only</option></select></Label>
      {f.kind === "video" && <Label t="Video"><select value={f.post_n} onChange={(e) => set("post_n", e.target.value)} className={inp} data-testid="fb-sched-add-post">{posts.map((p) => <option key={p.n} value={p.n}>Post {p.n}</option>)}</select></Label>}
      {f.kind === "picture" && <Label t="Picture"><select value={f.picture_id} onChange={(e) => set("picture_id", e.target.value)} className={inp} data-testid="fb-sched-add-picture"><option value="">Choose…</option>{pics.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}</select></Label>}
      {f.kind === "text" && <div />}
      <Label t="Time"><input type="time" value={f.time} onChange={(e) => set("time", e.target.value)} className={inp} data-testid="fb-sched-add-time" /></Label>
      <div className="flex items-end"><button onClick={submit} className={`${btnMain} w-full justify-center`} data-testid="fb-sched-add-submit"><Plus size={15} />Add</button></div>
      <Label t="Caption (leave empty to use the post's caption)" className="md:col-span-6"><textarea rows={2} value={f.caption} onChange={(e) => set("caption", e.target.value)} className={inp} data-testid="fb-sched-add-caption" /></Label>
    </div>
  );
};

const SeriesForm = ({ pages, onDone }) => {
  const [f, setF] = useState({ page_id: "", start: shiftDay(localToday(), 1), time: "19:00", kind: "video" });
  const [busy, setBusy] = useState(false);
  const go = async () => {
    const page_id = f.page_id || pages[0]?.id;
    if (!page_id) return toast.error("Add a page first");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/fb/schedule/series", { ...f, page_id, from_n: 19, tz_offset: new Date().getTimezoneOffset() });
      await onDone();
      const what = f.kind === "picture" ? "photo posts" : "new videos";
      toast.success(data.added ? `Scheduled ${data.added} ${what}, one a day until ${data.last_date}` : `All ${what} are already scheduled on this page`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule"); } finally { setBusy(false); }
  };
  return (
    <div className={`${card} grid gap-3 border-[#bfe6dd] bg-[#f3fbf9] md:grid-cols-5`} data-testid="fb-series-form">
      <div className="md:col-span-5 text-[14px] font-semibold text-[#0f2e2a]">Schedule a series — one a day</div>
      <Label t="What"><select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value, time: e.target.value === "picture" ? "12:00" : "19:00" })} className={inp} data-testid="fb-series-kind"><option value="video">New videos (Posts 19+)</option><option value="picture">Photo posts</option></select></Label>
      <Label t="Page"><PageSelect pages={pages} value={f.page_id || pages[0]?.id || ""} onChange={(v) => setF({ ...f, page_id: v })} testid="fb-series-page" /></Label>
      <Label t="Start on"><input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className={inp} data-testid="fb-series-start" /></Label>
      <Label t="Post time"><input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className={inp} data-testid="fb-series-time" /></Label>
      <div className="flex items-end"><button onClick={go} disabled={busy} className={`${btnMain} w-full justify-center`} data-testid="fb-series-submit">{busy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}Schedule</button></div>
      <p className="md:col-span-5 text-[12px] text-[#6b7c79]">Tip: photos at 12 PM and videos at 7 PM keep your page active twice a day. Each post gets an email reminder if you switch "Email me" on.</p>
    </div>
  );
};

const AutoDaily = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setEmail(data.post_email || data.alert_email || "")).catch(() => {}); }, []);
  const fill = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/fb/schedule/extend"); await onDone(); toast.success(data.added ? `${data.added} posts added` : "Next 14 days are already full"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not fill"); } finally { setBusy(false); }
  };
  return (
    <div className={`${card} flex flex-wrap items-center gap-3 border-[#bfe6dd] bg-[#f3fbf9]`} data-testid="fb-auto-daily">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0e8c7f] text-white"><Mail size={18} /></span>
      <div className="min-w-0 flex-1 basis-[220px] text-[13.5px] text-[#2a4b46]">
        <div className="text-[15px] font-bold text-[#0f2e2a]">Daily posting is on</div>
        Every day: a <b>picture at 12:00 PM</b> and a <b>video at 7:00 PM</b> (Eastern). The next 14 days refill by themselves.
        An email with the file and caption goes to <b className="break-all" data-testid="fb-auto-email">{email || "your email"}</b> at each posting time. Change it in Settings.
      </div>
      <button onClick={fill} disabled={busy} className={`${btnMain} w-full justify-center sm:w-auto`} data-testid="fb-auto-fill">{busy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}Fill next 14 days now</button>
    </div>
  );
};

export default function ScheduleSection({ pages }) {
  const { items, add, update, remove, reload } = useFb("schedule");
  const pics = useFb("pictures").items;
  const [posts, setPosts] = useState([]);
  const [day, setDay] = useState(localToday());
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/social").then(({ data }) => setPosts(data.posts || [])).catch(() => {}); }, []);
  const list = useMemo(() => items.filter((s) => s.date === day).sort((a, b) => a.time.localeCompare(b.time)), [items, day]);
  const done = list.filter((s) => s.posted).length;
  const autofill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/fb/schedule/autofill", { start: day, days: 7, tz_offset: new Date().getTimezoneOffset(), replace: true });
      await reload();
      toast.success(`Planned 7 days — ${data.added} posts added`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not plan"); } finally { setBusy(false); }
  };
  const pageOf = (id) => pages.find((p) => p.id === id);
  return (
    <div className="space-y-4" data-testid="fb-schedule-section">
      <AutoDaily onDone={reload} />
      <div className={`${card} flex flex-wrap items-center gap-3`}>
        <button onClick={() => setDay(shiftDay(day, -1))} className={btn} aria-label="Previous day" data-testid="fb-sched-prev"><ChevronLeft size={15} /></button>
        <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="rounded-lg border border-[#dcebe8] px-3 py-1.5 text-[14px]" data-testid="fb-sched-date" />
        <button onClick={() => setDay(shiftDay(day, 1))} className={btn} aria-label="Next day" data-testid="fb-sched-next"><ChevronRight size={15} /></button>
        <button onClick={() => setDay(localToday())} className={btn} data-testid="fb-sched-today">Today</button>
        <span className="text-[13.5px] text-[#5b6d6a]" data-testid="fb-sched-progress">{done} of {list.length} posted</span>
        <button onClick={autofill} disabled={busy} className={`${btnMain} ml-auto`} data-testid="fb-sched-autofill">{busy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}Plan next 7 days</button>
      </div>
      <BestTimeCard />
      <p className="text-[12.5px] text-[#6b7c79]">"Plan next 7 days" gives every active page one video and one picture a day, rotated so pages don't share the same post. Posts you already marked Posted are kept.</p>
      {list.length === 0 ? <Empty testid="fb-sched-empty">Nothing planned for this day. Tap "Plan next 7 days" or add a post below.</Empty>
        : list.map((s) => <Item key={s.id} s={s} page={pageOf(s.page_id)} posts={posts} pics={pics} update={update} remove={remove} />)}
      <SeriesForm pages={pages} onDone={reload} />
      <AddForm day={day} pages={pages} posts={posts} pics={pics} onAdd={add} />
    </div>
  );
}
